import React, { Component } from "react";
import PropTypes from "prop-types";
import { css, StyleSheet } from "aphrodite";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging - only in development
    if (process.env.NODE_ENV === 'development') {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log the error to an error reporting service here
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className={css(styles.errorContainer)}>
          <div className={css(styles.errorContent)}>
            <div className={css(styles.errorIcon)}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#e74c3c"
                  strokeWidth="2"
                />
                <line
                  x1="15"
                  y1="9"
                  x2="9"
                  y2="15"
                  stroke="#e74c3c"
                  strokeWidth="2"
                />
                <line
                  x1="9"
                  y1="9"
                  x2="15"
                  y2="15"
                  stroke="#e74c3c"
                  strokeWidth="2"
                />
              </svg>
            </div>
            
            <h3 className={css(styles.errorTitle)}>
              {this.props.title || "Bir hata oluştu"}
            </h3>
            
            <p className={css(styles.errorMessage)}>
              {this.props.message || 
               "Bu bölüm yüklenirken bir sorun yaşandı. Lütfen sayfayı yenileyin veya daha sonra tekrar deneyin."}
            </p>

            {this.props.showRetry !== false && (
              <button 
                className={css(styles.retryButton)}
                onClick={this.handleRetry}
              >
                Tekrar Dene
              </button>
            )}

            {/* Show detailed error in development */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className={css(styles.errorDetails)}>
                <summary className={css(styles.errorSummary)}>
                  Hata Detayları (Development)
                </summary>
                <pre className={css(styles.errorStack)}>
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  title: PropTypes.string,
  message: PropTypes.string,
  showRetry: PropTypes.bool,
  onError: PropTypes.func,
  onRetry: PropTypes.func,
};

const styles = StyleSheet.create({
  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "200px",
    padding: "20px",
    backgroundColor: "#fff",
    border: "1px solid #f1c0c7",
    borderRadius: "8px",
    margin: "20px 0",
  },
  errorContent: {
    textAlign: "center",
    maxWidth: "400px",
  },
  errorIcon: {
    marginBottom: "16px",
    display: "flex",
    justifyContent: "center",
  },
  errorTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#721c24",
    margin: "0 0 12px 0",
  },
  errorMessage: {
    fontSize: "14px",
    color: "#721c24",
    lineHeight: "1.5",
    margin: "0 0 20px 0",
  },
  retryButton: {
    backgroundColor: "#88c000",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: "#7ab000",
    },
    ":active": {
      backgroundColor: "#6a9900",
    },
  },
  errorDetails: {
    marginTop: "20px",
    textAlign: "left",
    border: "1px solid #e1e1e1",
    borderRadius: "4px",
    overflow: "hidden",
  },
  errorSummary: {
    padding: "12px",
    backgroundColor: "#f8f9fa",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    color: "#495057",
    ":hover": {
      backgroundColor: "#e9ecef",
    },
  },
  errorStack: {
    padding: "12px",
    margin: "0",
    fontSize: "11px",
    lineHeight: "1.4",
    color: "#721c24",
    backgroundColor: "#fff",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    fontFamily: "Monaco, Consolas, 'Courier New', monospace",
  },
});

export default ErrorBoundary;


// WEBPACK FOOTER //
// ./src/components/shared/ErrorBoundary.js